import { axiosClient } from '@/libs/axios-client';

/**
 * Retrieves dashboard statistics for the current user.
 *
 * This function sends a GET request to the `/dashboard/stats` endpoint
 * and returns the dashboard data and UI flags based on the user's role.
 *
 * The backend determines what data and UI permissions are available.
 *
 * @param {string} [companyId] - Optional company ID to filter statistics by specific company
 * @returns {Promise<Object>} A promise that resolves with:
 *   - `data`: Statistics object (totalEmployees, activeEmployees, etc.)
 *   - `uiFlags`: UI permission flags (canViewAllStats, canManageEmployees, etc.)
 * @throws {Error} Throws an error if the API call fails.
 */
export const getDashboardStats = async (companyId) => {
  try {
    const params = {};
    if (companyId) {
      params.companyId = companyId;
    }
    const response = await axiosClient.get('/dashboard/stats', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch dashboard stats:', error);
    throw error;
  }
};

/**
 * Retrieves absence seasonality data for Client dashboard charts.
 *
 * This function sends a GET request to the `/dashboard/absence-seasonality` endpoint
 * with query parameters for period type, year filter, and optional company filter.
 *
 * @param {string} periodType - 'monthly' or 'quarterly' (default: 'monthly')
 * @param {string} yearFilter - 'current' or 'all' (default: 'current')
 * @param {string} [companyId] - Optional company ID to filter data by specific company
 * @returns {Promise<Object>} A promise that resolves with:
 *   - `periods`: Array of period objects with absence hours by work type
 *   - `workTypes`: Array of work type metadata (key, label, color)
 * @throws {Error} Throws an error if the API call fails.
 */
export const getAbsenceSeasonality = async (periodType = 'monthly', yearFilter = 'current', companyId) => {
  try {
    const params = { periodType, yearFilter };
    if (companyId) {
      params.companyId = companyId;
    }
    const response = await axiosClient.get('/dashboard/absence-seasonality', { params });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch absence seasonality:', error);
    throw error;
  }
};

/**
 * Retrieves all absences for the logged-in employee.
 *
 * This function sends a GET request to the `/dashboard/employee/absences` endpoint
 * and returns the absence data aggregated by work type.
 *
 * @returns {Promise<Array>} A promise that resolves with array of absence objects:
 *   - `workType`: Work type key
 *   - `label`: Translated label
 *   - `code`: Short code
 *   - `hours`: Total hours
 *   - `days`: Total days (hours / 8)
 * @throws {Error} Throws an error if the API call fails.
 */
export const getEmployeeAbsences = async () => {
  try {
    const response = await axiosClient.get('/dashboard/employee/absences');
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch employee absences:', error);
    throw error;
  }
};

/**
 * Retrieves salary overview for current month for the logged-in employee.
 *
 * This function sends a GET request to the `/dashboard/employee/salary-overview` endpoint
 * and returns the salary overview for the current month.
 *
 * @returns {Promise<Object|null>} A promise that resolves with salary overview:
 *   - `status`: Salary status (WAITING, SUBMITTED, APPROVED)
 *   - `workHours`: Total work hours
 *   - `overTime`: Overtime 1 hours
 *   - `overTime2`: Overtime 2 hours
 *   - `totalOvertime`: Total overtime hours
 *   - `month`: Month name
 *   - `year`: Year
 * @throws {Error} Throws an error if the API call fails.
 */
export const getEmployeeSalaryOverview = async () => {
  try {
    const response = await axiosClient.get('/dashboard/employee/salary-overview');
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch employee salary overview:', error);
    throw error;
  }
};
